/**
 * ControlsFilterDrawer Component
 * 
 * Filter drawer for Control Activities (EX5)
 * Uses RightContentDrawer as base component
 * Uses search mode pattern for cycles and processes (like FinancialStatementItemDrawer)
 */

import React, { useState, useEffect } from 'react';
import { Button, Input } from 'antd';
import styled from 'styled-components';
import RightContentDrawer from '../../../components/common/RightContentDrawer';
import ButtonGroup from '../../../components/common/ButtonGroup';

// ========== TYPE DEFINITIONS ==========

export type WIPStatus = 'NotStarted' | 'WIP';
export type FinalCompletedStatus = 'NotStarted' | 'WIP' | 'Completed';

export interface UsersFilterState {
  email?: string,
  userName?: string,
  companyName?: string
}

export interface UsersFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: UsersFilterState) => void;
  currentFilters: UsersFilterState;
  annualReportId: string | null;
}

// ========== STYLED COMPONENTS ==========

const FilterSection = styled.div`
  margin-bottom: 24px;
`;

const FilterLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  color: #1F2937;
  margin-bottom: 8px;
`;

const StyledInput = styled(Input)`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  border-radius: 8px !important;
  letter-spacing: 0.005em;
  color: #6B7280;
  height: 44px;

  &.ant-input {
    color: #6B7280;
  }

  &::placeholder {
    color: #9CA3AF;
  }

  &:disabled {
    background: #F9FAFB;
    color: #9CA3AF;
    cursor: not-allowed;
  }
`;
// ========== HELPER FUNCTIONS ==========

const getActiveFilterCount = (filters: UsersFilterState): number => {
  let count = 0;
  if (filters.email?.length) count++;
  if (filters.userName?.length) count++;
  if (filters.companyName?.length) count++;
  return count;
};

const getEmptyFilters = (): UsersFilterState => ({
  email: undefined,
  userName: undefined,
  companyName: undefined,
});

// ========== MAIN COMPONENT ==========

const UsersFilterDrawer: React.FC<UsersFilterDrawerProps> = ({
  open,
  onClose,
  onApply,
  currentFilters,
}) => {
  const [localFilters, setLocalFilters] = useState<UsersFilterState>(currentFilters);
  

  // Sync local filters when drawer opens or current filters change
  useEffect(() => {
    if (open) {
      setLocalFilters(currentFilters);
    }
  }, [open, currentFilters]);

  
  
  const handleClearAll = () => {
    const emptyFilters = getEmptyFilters();
    setLocalFilters(emptyFilters);
    onApply(emptyFilters);
    onClose();
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleDrawerClose = () => {
    
    onClose();
    
  };

  const activeCount = getActiveFilterCount(localFilters);

  // Render filter UI
  const renderFilterUI = () => (
    <>
        <FilterSection>
            <FilterLabel>User Name</FilterLabel>
            <StyledInput
                value={localFilters.userName}
                onChange={(e) => {
                    const value = e.target.value;
                    setLocalFilters(prev => ({ ...prev, userName: value }))
                }}
                placeholder="Enter user name query"
            />
        </FilterSection>

        <FilterSection>
            <FilterLabel>Email</FilterLabel>
            <StyledInput
                value={localFilters.email}
                onChange={(e) => {
                    const value = e.target.value;
                    setLocalFilters(prev => ({ ...prev, email: value }))
                }}
                placeholder="Enter email query"
            />
        </FilterSection>

        <FilterSection>
            <FilterLabel>Company Name</FilterLabel>
            <StyledInput
                value={localFilters.companyName}
                onChange={(e) => {
                    const value = e.target.value;
                    setLocalFilters(prev => ({ ...prev, companyName: value }))
                }}
                placeholder="Enter company name query"
            />
        </FilterSection>

    </>
  );

  return (
    <RightContentDrawer
      open={open}
      onClose={handleDrawerClose}
      title= {'Filter'}
      width={600}
      resizable={true}
      footer={
        
        <ButtonGroup>
        {activeCount > 0 ? (
            <Button type="default" onClick={handleClearAll}>
            Clear Filter
            </Button>
        ) : (
            <Button type="default" onClick={onClose}>
            Cancel
            </Button>
        )}
        <Button type="primary" onClick={handleApply}>
            Apply Filters
        </Button>
        </ButtonGroup>
        
      }
    >
      {renderFilterUI()}
    </RightContentDrawer>
  );
};

export default UsersFilterDrawer;
